import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, UserPlus, Mail, Shield, Eye, Edit3, Trash2,
  Clock, Activity, ChevronDown, Search, Filter,
  RefreshCw, Send, X, Check, AlertCircle, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  admin: 'bg-red-500/20 text-red-400 border-red-500/30',
  owner: 'bg-red-500/20 text-red-400 border-red-500/30',
  editor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  member: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  viewer: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const ROLE_ICONS = {
  admin: Shield,
  owner: Shield,
  editor: Edit3,
  member: Edit3,
  viewer: Eye,
};

const ACTIVITY_ICONS = {
  user_invited: UserPlus,
  user_joined: Users,
  user_removed: Trash2,
  user_role_changed: Shield,
  lead_created: Users,
  lead_updated: Edit3,
  funnel_created: Activity,
  default: Activity,
};

const Team = () => {
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [myPermissions, setMyPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer', message: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [membersRes, statsRes, permissionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/team/members`, { headers }),
        axios.get(`${BACKEND_URL}/api/team/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/team/permissions`, { headers }),
      ]);

      setMembers(membersRes.data);
      setStats(statsRes.data);
      setMyPermissions(permissionsRes.data);

      // Fetch invites and activities if user has permission
      if (permissionsRes.data.permissions.manage_team) {
        const [invitesRes, activitiesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/team/invites`, { headers }),
          axios.get(`${BACKEND_URL}/api/team/activity?limit=50`, { headers }),
        ]);
        setInvites(invitesRes.data.filter(i => i.status === 'pending'));
        setActivities(activitiesRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/team/invite`,
        inviteForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'viewer', message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleUpdateRole = async (newRole) => {
    if (!selectedMember) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${BACKEND_URL}/api/team/members/${selectedMember.id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${selectedMember.name}'s role to ${newRole}`);
      setShowRoleModal(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name} from the team?`)) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/team/members/${member.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${member.name} has been removed from the team`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${BACKEND_URL}/api/team/invites/${inviteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invite cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${BACKEND_URL}/api/team/invites/${inviteId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invite resent');
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canManageTeam = myPermissions?.permissions?.manage_team;

  return (
    <div data-testid="team-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Team Management</h1>
          <p className="text-emerald-300/70">Manage your team members and permissions</p>
        </div>
        {canManageTeam && (
          <Button
            data-testid="invite-member-btn"
            onClick={() => setShowInviteModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_members}</p>
                  <p className="text-xs text-emerald-300/70">Total Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Shield className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.admins}</p>
                  <p className="text-xs text-emerald-300/70">Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Edit3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.editors}</p>
                  <p className="text-xs text-emerald-300/70">Editors</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500/20 rounded-lg">
                  <Eye className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.viewers}</p>
                  <p className="text-xs text-emerald-300/70">Viewers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending_invites}</p>
                  <p className="text-xs text-emerald-300/70">Pending Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['members', 'invites', 'activity'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab
                ? 'bg-emerald-600 text-white'
                : 'bg-[#052e22]/50 text-emerald-300/70 hover:bg-emerald-900/30'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'invites' && invites.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                {invites.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'members' && (
          <motion.div
            key="members"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-white">Team Members</CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg text-white placeholder-emerald-500/50 text-sm focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchData}
                      className="text-emerald-400 hover:bg-emerald-900/30"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
                  </div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No team members found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {filteredMembers.map((member) => {
                      const RoleIcon = ROLE_ICONS[member.role] || Eye;
                      return (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-4 hover:bg-emerald-900/20 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              {member.avatar ? (
                                <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <span className="text-emerald-400 font-medium">
                                  {member.name.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-sm text-emerald-300/70">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={`${ROLE_COLORS[member.role]} flex items-center gap-1`}>
                              <RoleIcon className="w-3 h-3" />
                              {member.role}
                            </Badge>
                            <span className="text-xs text-emerald-300/50">
                              Joined {formatDate(member.joined_at)}
                            </span>
                            {canManageTeam && member.role !== 'owner' && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setShowRoleModal(true);
                                  }}
                                  className="text-emerald-400 hover:bg-emerald-900/30"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveMember(member)}
                                  className="text-red-400 hover:bg-red-900/30"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'invites' && (
          <motion.div
            key="invites"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <CardTitle className="text-lg font-semibold text-white">Pending Invitations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {invites.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No pending invitations</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {invites.map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between p-4 hover:bg-emerald-900/20 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{invite.email}</p>
                            <p className="text-sm text-emerald-300/70">
                              Invited by {invite.invited_by_name} • {formatDate(invite.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge className={ROLE_COLORS[invite.role]}>
                            {invite.role}
                          </Badge>
                          <span className="text-xs text-amber-400">
                            Expires {formatDate(invite.expires_at)}
                          </span>
                          {canManageTeam && (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendInvite(invite.id)}
                                className="text-emerald-400 hover:bg-emerald-900/30"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelInvite(invite.id)}
                                className="text-red-400 hover:bg-red-900/30"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="bg-[#052e22]/50 border-emerald-800/30">
              <CardHeader className="border-b border-emerald-800/30">
                <CardTitle className="text-lg font-semibold text-white">Activity Log</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {activities.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-emerald-800 mx-auto mb-3" />
                    <p className="text-emerald-300/50">No activity yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-emerald-800/30">
                    {activities.map((activity) => {
                      const ActivityIcon = ACTIVITY_ICONS[activity.activity_type] || ACTIVITY_ICONS.default;
                      return (
                        <div
                          key={activity.id}
                          className="flex items-start gap-4 p-4 hover:bg-emerald-900/20 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <ActivityIcon className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white">{activity.description}</p>
                            <p className="text-sm text-emerald-300/70 mt-1">
                              by {activity.user_name} • {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Invite Team Member</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="colleague@company.com"
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    Role
                  </label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="admin">Admin - Full access</option>
                    <option value="editor">Editor - Create & edit content</option>
                    <option value="viewer">Viewer - Read-only access</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-emerald-300 mb-2">
                    Personal Message (optional)
                  </label>
                  <textarea
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                    placeholder="Welcome to the team!"
                    rows={3}
                    className="w-full px-4 py-3 bg-[#052e22] border border-emerald-800/50 rounded-lg text-white placeholder-emerald-500/50 focus:outline-none focus:border-emerald-500 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Role Change Modal */}
      <AnimatePresence>
        {showRoleModal && selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRoleModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a3d2e] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Change Role</h2>
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="p-2 hover:bg-emerald-900/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-emerald-400" />
                </button>
              </div>
              <p className="text-emerald-300/70 mb-6">
                Select a new role for <span className="text-white font-medium">{selectedMember.name}</span>
              </p>
              <div className="space-y-3">
                {['admin', 'editor', 'viewer'].map((role) => {
                  const RoleIcon = ROLE_ICONS[role];
                  const isCurrentRole = selectedMember.role === role;
                  return (
                    <button
                      key={role}
                      onClick={() => !isCurrentRole && handleUpdateRole(role)}
                      disabled={isCurrentRole}
                      className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                        isCurrentRole
                          ? 'border-emerald-500 bg-emerald-500/10 cursor-default'
                          : 'border-emerald-800/30 hover:border-emerald-600 hover:bg-emerald-900/30'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${ROLE_COLORS[role].replace('text-', 'bg-').replace('/20', '/30')}`}>
                        <RoleIcon className={`w-5 h-5 ${ROLE_COLORS[role].split(' ')[1]}`} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium capitalize">{role}</p>
                        <p className="text-sm text-emerald-300/70">
                          {role === 'admin' && 'Full access to all features'}
                          {role === 'editor' && 'Create and edit content'}
                          {role === 'viewer' && 'Read-only access'}
                        </p>
                      </div>
                      {isCurrentRole && (
                        <Badge className="bg-emerald-500/20 text-emerald-400">Current</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Team;
